// import MikeTooltip from '@mike/mike-shared-frontend/mike-tooltip/MikeTooltip'
import React from 'react'
import PropTypes from 'prop-types'
import { cloneElement, useMemo } from 'react'
import messages from '../../shared/messages'
import { ReactComponent as Projects } from '@mike/mike-shared-frontend/media/icons/Projects'
import { ReactComponent as Delete } from '@mike/mike-shared-frontend/media/icons/Delete'
import { Tabs, Tab } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useIntl } from 'react-intl'
import { useLocation, Link } from 'react-router-dom'

export const StyledTabsRoot = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    height: 3
  },
  borderBottom: '1px solid' + theme.palette.grey[300],
  paddingLeft: '1rem'
}))

export const ActiveLabel = styled('span')(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.secondary.main
}))

export const activeSvgStyle = {
  filter: 'invert(0.5) sepia(1) saturate(5) hue-rotate(175deg)'
}

const AppNav = ({ tenantId }) => {
  const intl = useIntl()

  function usePath() {
    return useLocation().pathname
  }

  const currentPath = usePath()

  // Tabs are keyed by name; `false` is MUI's "no tab selected".
  const activeTab = useMemo(() => {
    if (currentPath.startsWith('/site') || currentPath.startsWith('/project')) {
      return 'siteData'
    }

    if (currentPath === `/recycle-bin/${tenantId}`) {
      return 'recycleBin'
    }

    return false
  }, [tenantId, currentPath])

  return (
    <StyledTabsRoot
      // Only this nav; MemberReportDialog shares StyledTabsRoot at 1rem.
      sx={{ paddingLeft: '30px' }}
      value={activeTab}
      indicatorColor="secondary"
      textColor="primary"
    >
      <LinkTab
        value="siteData"
        to={'/site'}
        label={
          activeTab === 'siteData' ? (
            <ActiveLabel>{intl.formatMessage(messages.siteData)}</ActiveLabel>
          ) : (
            <span>{intl.formatMessage(messages.siteData)}</span>
          )
        }
        icon={
          <Projects
            style={activeTab === 'siteData' ? activeSvgStyle : undefined}
          />
        }
      />
      <LinkTab
        value="recycleBin"
        to={'/recycle-bin/' + tenantId}
        label={
          activeTab === 'recycleBin' ? (
            <ActiveLabel>{intl.formatMessage(messages.recycleBin)}</ActiveLabel>
          ) : (
            <span>{intl.formatMessage(messages.recycleBin)}</span>
          )
        }
        icon={
          <Delete
            style={activeTab === 'recycleBin' ? activeSvgStyle : undefined}
          />
        }
      />
    </StyledTabsRoot>
  )
}
AppNav.propTypes = {
  tenantId: PropTypes.string
}

export const StyledLinkTab = styled(Tab)<{
  component?: React.ElementType
  to?: string
}>({
  textTransform: 'capitalize',
  fontSize: '1em',
  opacity: 1,
  minHeight: 48,
  paddingLeft: '8px',
  paddingRight: '8px',
  margin: 0,
  '& .MuiTab-iconWrapper, & .MuiSvgIcon-root, & svg': {
    width: 24,
    height: 24,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 6
  }
})

// Tabs clones its children with `selected`, `onChange`, `indicator` and the
// like; they are passed through so the MUI Tab gets aria-selected and the
// roving tabIndex that makes the tab row reachable by keyboard.
export const LinkTab = ({
  to,
  label,
  icon,
  value = undefined as string | undefined,
  disabled = false,
  ...injected
}) => {
  // Tabs also passes its own textColor, which greys out the inactive tabs. Left
  // out, Tab falls back to inherit; the active tab is styled by ActiveLabel.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { textColor, ...tabProps } = injected as Record<string, unknown>
  const iconType = icon?.type
  const isMuiIcon =
    iconType?.muiName === 'SvgIcon' ||
    iconType?.render?.muiName === 'SvgIcon' ||
    iconType?.type?.render?.muiName === 'SvgIcon'
  return (
    <StyledLinkTab
      {...tabProps}
      disabled={disabled}
      component={Link}
      value={value}
      to={to}
      label={label}
      icon={isMuiIcon ? icon : cloneElement(icon, { viewBox: '8 8 24 24' })}
      iconPosition="start"
    />
  )
}

LinkTab.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.object.isRequired,
  icon: PropTypes.object.isRequired,
  value: PropTypes.any,
  disabled: PropTypes.bool
}

export default AppNav
